'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { loginOrRegister } from '@libs/api-proxy-client';
import type { IdentityUser } from '@libs/common-next';
import {
    clearAutoshipEligibilityBannerImpression,
    decodeJwtCookie,
    getENV,
    useCart,
    useDecisions,
    usePageLayout,
} from '@libs/common-next';
import type { CartUI } from '@libs/common-node';
import { AUTH_COOKIES } from '@libs/core';
import { loginExToken } from '@libs/experience-api';

function getRedirectUrl(pathname?: string) {
    const treatsPath = '/treats-rewards/authenticated';

    if (pathname === '/sign-in') {
        return treatsPath;
    }

    return pathname || treatsPath;
}

export function SignInSSO() {
    const { user, setUser } = usePageLayout();
    const pathname = usePathname() || undefined;
    const router = useRouter();
    const { setCart, enrichCart, updateSubscriptionStatus } = useCart();
    const { auth0 } = useDecisions();
    const searchParams = useSearchParams() || undefined;
    const exchangeToken = searchParams?.get('extok') || undefined;

    // TODO: loading state.  Included for use if needed
    const [_, setIsFetching] = useState(false);

    useEffect(() => {
        if (!exchangeToken || auth0) {
            return;
        }

        async function processExToken() {
            setIsFetching(true);

            try {
                const loginRes = await loginExToken({ user, exchangeToken });

                if (loginRes && 'accessToken' in loginRes) {
                    setUser(decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN));

                    const accountResponse = await loginOrRegister();
                    const updatedCart = accountResponse?.updatedCart;
                    if (updatedCart?.id) {
                        let cartToCommit = updatedCart as CartUI;
                        const discountUpdatesEnabled = Boolean(getENV('NEXT_PUBLIC_AUTOSHIP_DISCOUNT_UPDATES_ENABLED'));

                        if (discountUpdatesEnabled && cartToCommit.version) {
                            const refreshedCart = await updateSubscriptionStatus(cartToCommit.id, cartToCommit.version);
                            if (refreshedCart?.id) {
                                cartToCommit = refreshedCart;
                            }
                        }

                        const enrichedCart = await enrichCart(cartToCommit, undefined);
                        setCart(enrichedCart);
                    }

                    clearAutoshipEligibilityBannerImpression();
                    router.push(getRedirectUrl(pathname));
                } else {
                    setIsFetching(false);
                }
            } catch (error) {
                setIsFetching(false);
            }
        }

        void processExToken();
    }, [auth0, enrichCart, exchangeToken, pathname, router, setCart, setUser, updateSubscriptionStatus, user]);

    return null;
}
